import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi, type AdminQuestionReview } from "@/lib/adminApi";

export default function AdminAttemptDetail() {
  const { attemptId = "" } = useParams();
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ["admin-attempt", attemptId],
    queryFn: () => adminApi.getAttempt(attemptId),
  });

  const [rejectReasonFor, setRejectReasonFor] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const decide = useMutation({
    mutationFn: ({ docId, decision, rejectionReason }:
      { docId: string; decision: "Verify" | "Reject"; rejectionReason?: string }) =>
      adminApi.decideId(attemptId, docId, decision, rejectionReason),
    onSuccess: () => {
      setRejectReasonFor(null); setReason("");
      qc.invalidateQueries({ queryKey: ["admin-attempt", attemptId] });
    },
  });

  if (detail.isLoading) return <Centered>Loading…</Centered>;
  if (detail.isError) return <Centered>Could not load attempt.</Centered>;
  const d = detail.data!;

  return (
    <main className="mx-auto max-w-5xl p-6">
      <header className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-ink">
            {d.candidate.firstName} {d.candidate.lastName}
          </h1>
          <div className="text-sm text-brand-ink/60">
            {d.quizTitle} — {d.candidate.email} · {d.candidate.mobile}
          </div>
        </div>
        <a className="btn-secondary" href={adminApi.attemptPdfUrl(attemptId)} target="_blank">
          Download PDF
        </a>
      </header>

      {/* Score */}
      <div className="card mb-6 p-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          <Stat label="Final" value={d.finalScore} big />
          <Stat label="Positive" value={d.positiveTotal} />
          <Stat label="Negative" value={d.negativeTotal} />
          <Stat label="Penalty" value={d.penaltyTotal} />
          <Stat label="Result" value={d.passFail ?? "—"} />
        </div>
      </div>

      {/* ID docs */}
      {d.idDocuments.length > 0 && (
        <section className="card mb-6 p-6">
          <h2 className="mb-3 text-lg font-semibold text-brand-ink">ID verification</h2>
          {d.idDocuments.map((doc) => (
            <div key={doc.id} className="flex flex-wrap items-start gap-4 rounded-xl border border-brand-ink/10 p-4">
              <img src={doc.selfieSignedUrl} alt="selfie" className="h-32 w-32 rounded object-cover" />
              <a href={doc.idSignedUrl} target="_blank" className="text-brand hover:underline">
                {doc.idType} document
              </a>
              <div className="ml-auto">
                <div className="text-sm">Status: <b>{doc.verificationStatus}</b></div>
                {doc.rejectionReason && <div className="text-xs text-brand-ink/60">Reason: {doc.rejectionReason}</div>}
                {doc.verificationStatus === "Pending" && (
                  <div className="mt-2 flex gap-2">
                    <button className="btn-secondary" onClick={() => decide.mutate({ docId: doc.id, decision: "Verify" })}>
                      Verify
                    </button>
                    <button className="btn-secondary text-brand" onClick={() => setRejectReasonFor(doc.id)}>
                      Reject
                    </button>
                  </div>
                )}
              </div>
              {rejectReasonFor === doc.id && (
                <div className="mt-3 w-full">
                  <input className="field-input" placeholder="Reason for rejection"
                    value={reason} onChange={(e) => setReason(e.target.value)} />
                  <div className="mt-2 flex gap-2">
                    <button className="btn-primary"
                      onClick={() => decide.mutate({ docId: doc.id, decision: "Reject", rejectionReason: reason })}>
                      Confirm reject
                    </button>
                    <button className="btn-secondary" onClick={() => { setRejectReasonFor(null); setReason(""); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      {/* Question review */}
      <section className="card mb-6 p-6">
        <h2 className="mb-3 text-lg font-semibold text-brand-ink">Question review</h2>
        <div className="space-y-3">
          {d.questions.map((q) => <QuestionRow key={q.attemptQuestionId} q={q} />)}
        </div>
      </section>

      {/* Violations */}
      <section className="card mb-6 p-6">
        <h2 className="mb-3 text-lg font-semibold text-brand-ink">Violations ({d.violations.length})</h2>
        {d.violations.length === 0 ? (
          <p className="text-sm text-brand-ink/60">None recorded.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-brand-light text-left">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2 text-right">Penalty</th>
              </tr>
            </thead>
            <tbody>
              {d.violations.map((v) => (
                <tr key={v.id} className="border-t border-brand-ink/10">
                  <td className="px-3 py-2">{new Date(v.occurredAt).toLocaleString()}</td>
                  <td className="px-3 py-2">{v.type}</td>
                  <td className="px-3 py-2 text-right">{v.penaltyApplied}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Snapshot gallery */}
      <section className="card p-6">
        <h2 className="mb-3 text-lg font-semibold text-brand-ink">Camera snapshots ({d.snapshots.length})</h2>
        {d.snapshots.length === 0 ? (
          <p className="text-sm text-brand-ink/60">No snapshots captured.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 md:grid-cols-6">
            {d.snapshots.map((s) => (
              <a key={s.id} href={s.signedUrl} target="_blank" className="block">
                <img src={s.signedUrl} alt="" className="aspect-square w-full rounded object-cover" />
                <div className="mt-1 text-center text-xs text-brand-ink/60">
                  {new Date(s.capturedAt).toLocaleTimeString()}
                </div>
              </a>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function Stat({ label, value, big = false }: { label: string; value: number | string; big?: boolean }) {
  return (
    <div className="rounded-xl bg-brand-light p-3">
      <div className="text-xs uppercase tracking-wide text-brand-ink/60">{label}</div>
      <div className={`mt-1 font-bold text-brand-ink ${big ? "text-3xl" : "text-lg"}`}>{value}</div>
    </div>
  );
}

function QuestionRow({ q }: { q: AdminQuestionReview }) {
  return (
    <div className="rounded-xl border border-brand-ink/10 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="font-semibold text-brand-ink">
          {q.orderIndex + 1}. {q.questionText}
        </div>
        <div className="text-xs text-brand-ink/60 whitespace-nowrap">
          {q.categoryName} · {q.difficulty}
        </div>
      </div>
      <ul className="mt-3 space-y-1 text-sm">
        {q.options.map((o) => {
          const selected = q.selectedOptionId === o.id;
          const color = o.isCorrect ? "text-emerald-700"
            : selected ? "text-brand"
            : "text-brand-ink/80";
          const prefix = o.isCorrect ? "✓" : selected ? "✗" : "·";
          return (
            <li key={o.id} className={color}>{prefix} {o.text}</li>
          );
        })}
      </ul>
      <div className="mt-2 text-xs text-brand-ink/60">
        {q.isCorrect === null ? "Skipped"
          : q.isCorrect ? `+${q.positiveMarksApplied}`
          : `−${q.negativeMarksApplied}`}
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <main className="grid min-h-screen place-items-center p-6 text-brand-ink/60">{children}</main>;
}
